import React, { useContext, useEffect, useRef, useState } from "react";
import GlobalConfig from "../context/GlobalConfig";
import Footer from "../components/Footer";
import Head from "./Head";
import { languageData } from "../constants/languageData";
import JourneyConfig from "../context/JourneyConfig";
import RenderSvg from "../components/RenderSvg";
import Loader from "../components/Loader/Loader";
import { trackAction } from "../utils/hyperdx";

function VerificationComplete({ onNextStep, currentStep, stepperFunc, calledOnRefreash=false }) {
  const { config } = useContext(GlobalConfig);
  const { journey, setJourney, updateJourney } = useContext(JourneyConfig);
  
  // Helper function to validate and parse redirectTime as a number
  const validateRedirectTime = (value) => {
    if (value == null || value === "") return null;
    const parsed = Number.parseInt(value, 10);
    return (Number.isNaN(parsed) || parsed < 0) ? null : parsed;
  };

  // Get values: check journey.processingConfig first, then fallback to sessionStorage
  const successRedirectUrl = journey?.processingConfig?.successRedirectUrl ?? sessionStorage.getItem("successRedirectUrl");
  const storedStatusOption = config?.userSettings?.showStatusScreenBeforeRedirection ?? sessionStorage.getItem("showStatusScreenBeforeRedirection");
  const redirectTimeValue = journey?.processingConfig?.redirectTime ?? sessionStorage.getItem("redirectTime");
  const language = sessionStorage.getItem("language") ?? "en";
  // Validate redirectTime is a valid number
  const redirectTime = validateRedirectTime(redirectTimeValue);

  // On a completed-journey reload the flow config is not re-fetched, so `config` is empty and the
  // completion icon loses its brand accent color. Fall back to the branding persisted in
  // sessionStorage (same pattern as Expired/JourneyStopped) so the terminal page keeps its theme
  // after a refresh (SA-33713). During a live render config.branding exists, so this is a no-op.
  let brandedConfig = config;
  if (!config?.branding) {
    try {
      const storedBranding = JSON.parse(sessionStorage.getItem("branding"));
      if (storedBranding) brandedConfig = { ...config, branding: storedBranding };
    } catch (error) {
      brandedConfig = config;
    }
  }

  // Biometric Consent with Decline Option (IDX-676): when the applicant declined, show the
  // decline message instead of the standard success copy. On a normal render this is derived
  // from the live flow/journey state; after a page refresh at /complete the sessionId is
  // cleared and that state can no longer be restored, so we also honor a persisted flag set
  // at decline time (SA-33713) — keeping the decline message visible across a reload.
  // Applicant declined biometric verification but still completed KYC with submitted documents.
  const declinedBiometricConsent =
    (config?.faceCapture?.biometricConsent === true &&
      journey?.capturedData?.livenessConsent === false) ||
    sessionStorage.getItem("biometricDeclined") === "true";
  // The decline message is a fixed platform string, rendered localized to the journey's
  // language. It is resolved from the language pack (which survives a refresh), never from a
  // stored per-flow value, so it stays correct and translated even after a reload (SA-33663).
  const biometricDeclineMessage =
    languageData[language]?.biometricConsent?.declineComplete ||
    languageData["en"]?.biometricConsent?.declineComplete;

  console.log("journey?.processingConfig",journey?.processingConfig);
  console.log("showStatusScreenBeforeRedirection",config?.userSettings?.showStatusScreenBeforeRedirection)
  
  // State management
  const [showStatusScreenBeforeRedirection, setShowStatusScreenBeforeRedirection] = useState(
    storedStatusOption != null 
      ? (typeof storedStatusOption === "boolean" ? storedStatusOption : storedStatusOption === "true")
      : config?.userSettings?.showStatusScreenBeforeRedirection ?? true
  );
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(-1);
  const hasMarkedComplete = useRef(false);
  
  // Constants
  const defaultRedirectTime = 1;
  const shouldRedirect = !!successRedirectUrl;
  const shouldShowUI = !shouldRedirect || showStatusScreenBeforeRedirection;

  // Terminal outcome: verification completed.
  useEffect(() => {
    trackAction("journey:outcome", { outcome: "complete" });
  }, []);

  // Mark journey as complete on mount
  useEffect(() => {
    if (journey?.step !== "COMPLETE") {
      setJourney({ ...journey, step: "COMPLETE" });
    }
  }, [journey, setJourney]);

  // Handle post-verification logic
  useEffect(() => {
    const completeVerification = async () => {
      if (journey?.step === "COMPLETE" && !hasMarkedComplete.current) {
        const response = await updateJourney("COMPLETE", {}, currentStep);
        hasMarkedComplete.current = true;
        if(response?.error === true){
          stepperFunc(
            sessionStorage.getItem("currentStep")
              ? parseInt(sessionStorage.getItem("currentStep"))
              : 0
          );
          return;
        }
        window.parent.postMessage("Verification Complete", "*");
        
        // Set loading based on whether we need to show UI
        setLoading(!shouldShowUI);
        
        // Initialize countdown if redirect is needed
        if (shouldRedirect) {
          const initialCount = redirectTime != null ? redirectTime : defaultRedirectTime;
          setCount(initialCount);
        }
      }
    };

    completeVerification();
  }, [journey?.step]);

  // Handle countdown and redirection
  useEffect(() => {
    if (count < 0) return;
    
    // Safety check: if redirect URL is invalid, don't proceed with countdown
    if (shouldRedirect && !successRedirectUrl) {
      setCount(-1);
      return;
    }
    
    const timer = setTimeout(() => {
      if (count > 0) {
        setCount(count - 1);
      } else if (shouldRedirect && successRedirectUrl) {
        clearSessionStorage();
        window.location.href = successRedirectUrl;
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [count, shouldRedirect, successRedirectUrl]);

  // Clean up session storage
  const clearSessionStorage = () => {
    const itemsToRemove = [
      "sessionId", "jId", "currentStep", "redirectTime", "companyLogo",
      "document", "ipAddress", "selectedCountry", "failureRedirectUrl",
      "successRedirectUrl", "documentFailureReason", "showStatusScreenBeforeRedirection",
      "biometricDeclined"
    ];
    itemsToRemove.forEach(item => sessionStorage.removeItem(item));
  };

  // Show loader if still processing or if we shouldn't show UI
  if (loading || !shouldShowUI) {
    return <Loader />;
  }

  return (
    <>
      <Head
        style={{ zIndex: "100" }}
        showMobileFlow={false}
        stepperFunc={stepperFunc}
        onNextStep={onNextStep}
        showHelp={false}
        showLanguages={false}
      />
      <div className="parent-container">
        <div className="main">
          <div className="left-section">
            <RenderSvg
              config={brandedConfig}
              svgType={"verificationComplete"}
              id="verificationComplete"
              style={{ marginTop: "3rem" }}
            />
          </div>
          <div
            className="right-section md:items-start items-center"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 24,
              fontFamily: "Inter, sans-serif",
              
            }}
          >
            <div
              className="flex flex-col"
              style={{ gap: "16px", marginBottom: "auto" }}
            >
              <div className="text-[18px] md:text-[28px] font-semibold text-[#000000] text-center md:text-left">
                {languageData[language]?.successful?.title}
              </div>
              <div className="text-[12px] md:text-[16px] font-semibold text-[#656565] text-center md:text-left">
                {declinedBiometricConsent
                  ? biometricDeclineMessage
                  : languageData[language]?.successful?.text}
              </div>
              {successRedirectUrl && (
                <div className="text-[12px] md:text-[16px] font-semibold text-[#656565] text-center md:text-left">
                  {count > 0 ? (
                    <>
                      {languageData[language]?.redirect} {count}...{" "}
                      {languageData[language]?.redirectCta?.or ??
                        languageData["en"]?.redirectCta?.or}{" "}
                      <span
                        onClick={() => {
                          clearSessionStorage();
                          window.location.href = successRedirectUrl;
                        }}
                        style={{
                          color: "#1658aa",
                          cursor: "pointer",
                        }}
                      >
                        {languageData[language]?.redirectCta?.clickHere ??
                          languageData["en"]?.redirectCta?.clickHere}
                      </span>
                    </>
                  ) : (
                    <>
                      <span
                        onClick={() => {
                          clearSessionStorage();
                          window.location.href = successRedirectUrl;
                        }}
                        style={{
                          color: "#1658aa",
                          cursor: "pointer",
                        }}
                      >
                        {languageData[language]?.redirectCta?.clickHereStart ??
                          languageData["en"]?.redirectCta?.clickHereStart}
                      </span>
                      {" "}
                      {languageData[language]?.redirectCta?.toContinue ??
                        languageData["en"]?.redirectCta?.toContinue}
                    </>
                  )}
                </div>
              )}
            </div>
            <Footer />
          </div>
        </div>
      </div>
    </>
  );
}
export default VerificationComplete;
